import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLocation as useReactLocation, useNavigate as useReactNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useSwipeable } from "react-swipeable";
import {
    ArrowLeft,
    AlertCircle,
    Image,
    AlertTriangle,
    PlusCircle,
    Utensils,
} from "lucide-react";

import InfoDrink from "../../../components/Info/InfoDrink";
import SkeletonDettaglioProdotto from "../../../components/skeleton/SkeletonDettaglioProdotto";
import { getIconCategory } from "../../../utils/utils";

import productsApi from "../../../services/productsApi";

export default function DettaglioProdotto() {
    const navigate = useReactNavigate();
    const location = useReactLocation();

    // 🚀 1. FILTRO INTELLIGENTE: Consideriamo solo i prodotti con dettaglio attivo e disponibili
    const filteredProducts = useMemo(() => {
        const rawProducts = Array.isArray(location.state?.products) ? location.state.products : [];
        return rawProducts.filter(p => p.showDetailPage !== false && p.available !== false);
    }, [location.state?.products]);

    // 🚀 2. INDICE DINAMICO: Troviamo la posizione corretta nell'array filtrato usando l'ID di partenza
    const initialIndex = useMemo(() => {
        const originProducts = Array.isArray(location.state?.products) ? location.state.products : [];
        const originIndex = typeof location.state?.currentIndex === "number" ? location.state.currentIndex : 0;
        const currentOriginProduct = originProducts[originIndex];

        if (!currentOriginProduct) return 0;
        
        const foundIndex = filteredProducts.findIndex(p => p.id === currentOriginProduct.id);
        return foundIndex !== -1 ? foundIndex : 0;
    }, [location.state?.currentIndex, location.state?.products, filteredProducts]);

    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [product, setProduct] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const loadProductDetails = async (id) => {
        try {
            setIsLoading(true);
            const response = await productsApi.retrieveProduct(id);
            if (response?.data?.data) {
                setProduct(response.data.data);
            }
        } catch (err) {
            console.error("Errore nel recuperare i dettagli del prodotto:", err);
        } finally {
            setIsLoading(false);
        }
    };

    // Ascolta i cambi di indice sulla lista filtrata
    useEffect(() => {
        if (filteredProducts.length > 0 && filteredProducts[currentIndex]?.id) {
            loadProductDetails(filteredProducts[currentIndex].id);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [currentIndex, filteredProducts]);

    useEffect(() => {
        if (product && product.categoryId) {
            sessionStorage.setItem("activeCategory", product.categoryId);
        }
    }, [product]);

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            if (currentIndex < filteredProducts.length - 1) setCurrentIndex((prev) => prev + 1);
        },
        onSwipedRight: () => {
            if (currentIndex > 0) setCurrentIndex((prev) => prev - 1);
        },
        preventScrollOnSwipe: true,
        trackMouse: true,
    });

    if (isLoading || !product) return <SkeletonDettaglioProdotto />;

    const CategoryIcon = getIconCategory(product.categoryIcon);

    const ingredientList = typeof product.ingredients === "string" && product.ingredients.trim() !== ""
        ? product.ingredients.split(",").map(item => item.trim())
        : Array.isArray(product.ingredients) ? product.ingredients : [];

    return (
        <main className="min-h-safe-screen bg-[#090D16] text-secondary pb-32 pt-[64px] relative" {...swipeHandlers}>
            {/* Header Superiore */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#0D121F]/90 backdrop-blur-md px-4 py-3 border-b border-accent/40 shadow-sm">
                <div className="flex items-center justify-between max-w-md mx-auto">
                    <button
                        onClick={() => {
                            if (window.history.length > 2) {
                                navigate(-1);
                            } else {
                                navigate("/menu");
                            }
                        }}
                        className="text-secondary p-2 bg-surface/50 border border-accent/30 rounded-xl active:scale-95 transition-all"
                        aria-label="Torna al menu"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center justify-center gap-2.5 text-primary text-xs font-bold bg-primary/10 px-3.5 py-2 rounded-full border border-primary/20 max-w-[180px] h-8 shrink-0">
                        {CategoryIcon && <span className="flex items-center justify-center w-4 h-4 shrink-0 aspect-square text-primary">{CategoryIcon}</span>}
                        <span className="truncate leading-none pt-[1px]">{product.categoryName}</span>
                    </div>
                    <div className="w-9" />
                </div>
            </header>

            {/* Immagine */}
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="px-6 pt-6 flex justify-center">
                <div className="relative w-72 h-72 rounded-full overflow-hidden border-4 border-primary/80 bg-[#121824] shadow-[0_0_50px_rgba(233,69,69,0.25)]">
                    <div className="absolute inset-0 w-full h-full">
                        {product.image ? (
                            <img src={product.image} alt={product.title} crossOrigin="anonymous" className="w-full h-full object-cover" draggable={false} loading='eager' />
                        ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-muted gap-1 bg-background">
                                <Image className="w-6 h-6 opacity-40" />
                                <span className="text-[10px]">Nessuna foto</span>
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>

            {/* Titolo & Prezzo */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.4 }} className="mt-6 text-center px-6 max-w-md mx-auto space-y-2">
                <h1 className="text-2xl font-bold tracking-tight text-secondary leading-tight break-words">{product.title}</h1>
                <div className="inline-flex flex-col items-center pt-1">
                    <span className="text-3xl font-black tracking-tight text-primary">{product.price}</span>
                </div>
            </motion.div>

            {/* Dettagli Tecnici */}
            <div className="px-4 mt-6 max-w-md mx-auto">
                {product.type === "drink" && (
                    <InfoDrink price={null} categoryIcon={product.categoryIcon} alcoholPercentage={product.alcoholPercentage} quantity={product.quantity} isAvailable={product.available} />
                )}
            </div>

            {/* Corpo Informazioni */}
            <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="px-4 max-w-md mx-auto space-y-4 pt-4">
                {product.description && (
                    <div className="bg-[#121824] p-4 rounded-2xl border border-accent/20">
                        <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-2 flex items-center gap-2"><AlertCircle className="w-4 h-4 text-primary" /> Descrizione</h3>
                        <p className="text-sm text-secondary/80 leading-relaxed">{product.description}</p>
                    </div>
                )}

                {ingredientList.length > 0 && (
                    <div className="bg-[#121824] p-4 rounded-2xl border border-accent/20">
                        <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-3 flex items-center gap-2"><Utensils className="w-4 h-4 text-primary" /> Ingredienti</h3>
                        <div className="flex flex-wrap gap-2">
                            {ingredientList.map((item, i) => (
                                <span key={i} className="bg-background border border-accent/30 text-secondary/90 text-xs font-medium px-3 py-1.5 rounded-xl shadow-sm">{item}</span>
                            ))}
                        </div>
                    </div>
                )}

                {product.type !== "drink" && (
                    <div className="bg-[#121824] p-4 rounded-2xl border border-accent/20">
                        <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-1 flex items-center gap-2"><PlusCircle className="w-4 h-4 text-primary" /> Extra Personalizzabili</h3>
                        <p className="text-xs text-muted leading-relaxed">Ogni modifica o aggiunta di ingredienti potrebbe comportare una variazione del prezzo finale.</p>
                    </div>
                )}

                {product.allergens && (
                    <div className="bg-amber-500/5 p-4 rounded-2xl border border-amber-500/20">
                        <h3 className="text-sm font-bold uppercase tracking-wider text-amber-400 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> Attenzione Allergeni</h3>
                        <p className="text-xs text-amber-200/70 leading-relaxed bg-black/20 p-2.5 rounded-xl border border-amber-500/10">{product.allergens}</p>
                    </div>
                )}
            </motion.section>

            {/* Navigazione Inferiore Floating: Adesso mappa l'array filtrato */}
            <div className="fixed bottom-5 left-1/2 -translate-x-1/2 w-[92%] max-w-sm bg-[#0D121F]/90 backdrop-blur-md border border-accent/60 rounded-2xl p-2.5 flex justify-between items-center text-sm z-30 shadow-2xl shadow-black/80">
                <button
                    disabled={currentIndex === 0}
                    onClick={() => setCurrentIndex((prev) => prev - 1)}
                    className="flex items-center justify-center px-4 py-2.5 rounded-xl font-medium text-primary disabled:opacity-20 active:scale-95 transition-all"
                >
                    ← Indietro
                </button>

                <div className="bg-background/80 border border-accent/40 px-4 py-1.5 rounded-xl shadow-inner font-semibold text-xs tracking-widest text-muted">
                    {currentIndex + 1} <span className="opacity-40">/</span> {filteredProducts.length}
                </div>

                <button
                    disabled={currentIndex === filteredProducts.length - 1}
                    onClick={() => setCurrentIndex((prev) => prev + 1)}
                    className="flex items-center justify-center px-4 py-2.5 rounded-xl font-medium text-primary disabled:opacity-20 active:scale-95 transition-all"
                >
                    Avanti →
                </button>
            </div>
        </main>
    );
}